import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ConfigProvider, App as AntdApp, theme as antdTheme } from 'antd';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import NotFound from './pages/NotFound';
import ProtectedRoutes from './components/ProtectedRoutes';
import LoginWithIpCheck from './components/LoginWithIpCheck';
import './App.css';
import './styles/theme.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const AppContent: React.FC = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/404" element={<NotFound />} />
        <Route path="/login" element={<LoginWithIpCheck />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/404" element={<NotFound />} />
      <Route path="*" element={<ProtectedRoutes />} />
    </Routes>
  );
};

/** Bind Ant Design algorithm + solid tokens to app light/dark theme (CSS vars break sort hover). */
const ThemedConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#1890ff',
          borderRadius: 6,
          colorBgContainer: isDark ? '#242424' : '#ffffff',
          colorBgElevated: isDark ? '#242424' : '#ffffff',
          colorBgLayout: isDark ? '#1a1a1a' : '#ffffff',
          // Tooltip spotlight must stay dark so text (white) stays readable
          colorBgSpotlight: isDark ? '#2f2f2f' : '#1f1f1f',
          colorText: isDark ? '#ffffff' : '#1a1a1a',
          colorTextSecondary: isDark ? '#e0e0e0' : '#4a4a4a',
          colorTextTertiary: isDark ? '#a0a0a0' : '#8a8a8a',
          colorTextQuaternary: isDark ? '#666666' : '#c0c0c0',
          colorBorder: isDark ? '#404040' : '#e1e5e9',
          colorBorderSecondary: isDark ? '#333333' : '#f0f2f5',
          colorFill: isDark ? '#2f2f2f' : '#f5f5f5',
          colorFillSecondary: isDark ? '#3a3a3a' : '#f0f2f5',
          colorFillTertiary: isDark ? '#2f2f2f' : '#f1f3f4',
          colorFillQuaternary: isDark ? '#242424' : '#fafbfc',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
          fontSize: 14,
          lineHeight: 1.5,
        },
        components: {
          Button: {
            borderRadius: 6,
            fontWeight: 500,
          },
          Card: {
            borderRadius: 8,
          },
          Input: {
            borderRadius: 6,
          },
          Select: {
            borderRadius: 6,
          },
          Table: {
            borderRadius: 6,
            headerBg: isDark ? '#2f2f2f' : '#fafbfc',
            headerColor: isDark ? '#ffffff' : '#1a1a1a',
            headerSortActiveBg: isDark ? '#3a3a3a' : '#e6f4ff',
            headerSortHoverBg: isDark ? '#3a3a3a' : '#f0f2f5',
            bodySortBg: isDark ? '#2a2a2a' : '#fafafa',
            rowHoverBg: isDark ? '#2f2f2f' : '#f5f5f5',
          },
          Tooltip: {
            colorBgSpotlight: isDark ? '#2f2f2f' : '#1f1f1f',
            colorTextLightSolid: '#ffffff',
          },
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ThemedConfigProvider>
          <AntdApp>
            <AuthProvider>
              <Router>
                <AppContent />
              </Router>
            </AuthProvider>
          </AntdApp>
        </ThemedConfigProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
